// Imperative "fly to the AI processing queue" effect.
//
// When the user backgrounds an AI job (e.g. "Continue in the background"), this
// spawns a small file badge at `fromRect` and animates it in an arc toward the
// navbar AI-processing trigger button (id = AI_PROCESSING_TRIGGER_ID), then
// pulses that button. The purpose is to show the user *where* the job went and
// how to get back to it.
//
// It's a self-contained DOM + Web Animations API effect (no React) on purpose:
// the source modal usually unmounts right after the action, so the flying node
// is appended to <body> and animates independently. Respects reduced motion.
// Reusable across flows (certificate AI today; audit AI etc. later).

export const AI_PROCESSING_TRIGGER_ID = 'ai-processing-trigger';

interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface FlyToAiProcessingOptions {
  /** Source rect to fly from — usually the file chip's `getBoundingClientRect()`. */
  fromRect: RectLike;
  /** FontAwesome icon class for the flying badge. */
  iconClass?: string;
}

export function flyToAiProcessing({
  fromRect,
  iconClass = 'fa-regular fa-file',
}: FlyToAiProcessingOptions): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const target = document.getElementById(AI_PROCESSING_TRIGGER_ID);
  if (!target) return; // No queue button on screen — nothing to fly toward.

  const pulseTarget = () => {
    target.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.18)' },
        { transform: 'scale(1)' },
      ],
      { duration: 320, easing: 'ease-out' },
    );
  };

  const prefersReducedMotion = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (prefersReducedMotion) {
    pulseTarget();
    return;
  }

  const targetRect = target.getBoundingClientRect();
  const fromX = fromRect.left + fromRect.width / 2;
  const fromY = fromRect.top + fromRect.height / 2;
  const toX = targetRect.left + targetRect.width / 2;
  const toY = targetRect.top + targetRect.height / 2;
  const dx = toX - fromX;
  const dy = toY - fromY;

  const node = document.createElement('div');
  node.setAttribute('aria-hidden', 'true');
  Object.assign(node.style, {
    position: 'fixed',
    left: `${fromX}px`,
    top: `${fromY}px`,
    width: '44px',
    height: '44px',
    marginLeft: '-22px',
    marginTop: '-22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    background: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
    color: '#3f3f46',
    fontSize: '18px',
    zIndex: '9999',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
  });
  const icon = document.createElement('i');
  icon.className = iconClass;
  node.appendChild(icon);
  document.body.appendChild(node);

  // Lift the midpoint above the straight line for a "tossed into the queue" arc.
  const midX = dx * 0.5;
  const midY = dy * 0.5 - 80;

  const animation = node.animate(
    [
      { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
      {
        transform: `translate(${midX}px, ${midY}px) scale(0.7)`,
        opacity: 0.9,
        offset: 0.6,
      },
      {
        transform: `translate(${dx}px, ${dy}px) scale(0.2)`,
        opacity: 0,
        offset: 1,
      },
    ],
    {
      duration: 850,
      easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
      fill: 'forwards',
    },
  );
  animation.onfinish = () => {
    node.remove();
    pulseTarget();
  };
  animation.oncancel = () => node.remove();
}
